'use client';

import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslations } from 'next-intl';
import { Button } from '../../../../../packages/ui/components/button';
import { IcoMoonIcon } from '../../../../../packages/ui/components/icons';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import {
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import {
  sendGameExitEvent,
  sendPlayGameEvent,
} from '@wwe-portal/web/libs/analytics/play-game';
import { getTrackerInstance } from '@wwe-portal/web/libs/analytics/titan';
import { FacebookLogin } from '../auth/facebook-login';
import { ScopelyLogin } from '../auth/scopely-login';
import { isAndroid, isIOS } from '@wwe-portal/web/utils/platform/os';
import './game-section.css';
import { IFRAME_PLAY_URL } from '@wwe-portal/web/utils/config';
import Image from 'next/image';
import { LeavingPageDialog } from '../common/dialog/leaving-page-dialog';
import { useSearchParams } from 'next/navigation';
import { YoutubeDialog } from '@wwe-portal/ui/components/dialog/youtube-dialog';
import { showDialog } from '@wwe-portal/ui/components/dialog';

/*
  Takes one of the following as an input:
    localhost, wwechampions.com, dev.wwechampions.com, qa.wwechampions.com, staging.wwechampions.com
  Returns:
    localhost, wwechampions.com
*/
function getDocumentDomainFromHostname(hostname: string): string {
  const segments = hostname.split('.');
  if (segments.length === 3) {
    hostname = `${segments[1]}.${segments[2]}`;
  }
  return hostname;
}

export const GameSectionIntroductionForMobileApp = () => {
  const t = useTranslations();
  return (
    <>
      <div className="text-inlineTextColor12 mb-2.5 block text-base font-medium xl:hidden">
        {t('download_to_play')}
      </div>
      <div className="mt-4 flex w-full flex-col items-center justify-center gap-2 xl:hidden">
        {isAndroid() && (
          <a
            href="https://play.google.com/store/apps/details?id=com.scopely.whiplash&hl=en"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src="/assets/images/common/google-play.png"
              alt="playstore"
              width={170}
              height={66}
            />
          </a>
        )}
        {isIOS() && (
          <a
            href="https://apps.apple.com/app/wwe-champions-2020/id1017432937 "
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/assets/images/common/app-store.svg"
              alt="appstore"
              width={120}
              height={40}
            />
          </a>
        )}
      </div>
    </>
  );
};

export const GameSectionIntroductionForWeb: FC<{
  isLoggedIn: boolean;
  sessionLoading: boolean;
  onPlayClick: () => void;
}> = ({ isLoggedIn, sessionLoading, onPlayClick }) => {
  const t = useTranslations();
  const handlePlayClick = () => {
    if (sessionLoading) return;
    onPlayClick();
  };
  return (
    <>
      <div className="text-inlineTextColor12 mb-2.5 text-base font-medium">
        {isLoggedIn
          ? `${t('wwe_champions')} ${new Date().getFullYear()}`
          : t('play_game_not_loggedIn_title')}
      </div>
      <p className="text-chetwodeBlue mb-10 text-xs">
        {isLoggedIn
          ? t('play_game_loggedIn_description')
          : t('play_game_not_loggedIn_description')}
      </p>
      {isLoggedIn ? (
        <div className="flex items-center justify-center">
          <Button
            onClick={handlePlayClick}
            variant="blue"
            className="h-12 w-full max-w-[300px]"
            loading={sessionLoading}
          >
            <div className="flex w-full items-center justify-center gap-2">
              <IcoMoonIcon iconName="gamepad" className="size-5" />
              <span className="font-normal">{t('play_game')}</span>
            </div>
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-center">
          <div className="flex w-full max-w-[300px] flex-col gap-4">
            <FacebookLogin />
            <ScopelyLogin />
          </div>
        </div>
      )}
    </>
  );
};

type WebGLMessageData = {
  type?: 'RELOAD_PORTAL';
};

export const GameSection = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [startPlayGame, setStartPlayGame] = useState(false);
  const { isMobile } = useResponsive();
  const isLoggedIn = useIsAuthenticated();
  const sessionLoading = useIsAuthenticating();
  const currentUser = useGetCurrentUser();
  const search = useSearchParams();

  const shouldPlayOnMobileApp = isAndroid() || isIOS();

  const handlePlayGameClick = useCallback(() => {
    if (isLoggedIn && currentUser) {
      // Compatibility with old WebGL Builds (< 1/2026)
      window.session = currentUser.sessionToken;
      window.nonce = currentUser.nonce;
      window.deviceID = currentUser.deviceToken;
      window.deviceToken = getTrackerInstance().tracker.getDeviceToken();
      window.facebookID = currentUser.fbId;
      window.playerID = currentUser.playerId;
      window.alias = currentUser.playerId;

      // expected: wwechampions.com or localhost
      try {
        const domain = getDocumentDomainFromHostname(window.location.hostname);
        // could throw a security error if the domain doesn't match
        document.domain = domain;
      } catch (err) {
        console.error(err);
      }
      sendPlayGameEvent();
      setStartPlayGame(true);
    }
  }, [isLoggedIn, currentUser]);

  useEffect(() => {
    if (!isLoggedIn && iframeRef.current) {
      setStartPlayGame(false);
      iframeRef.current = null;
    }
  }, [isLoggedIn, iframeRef.current]);

  useEffect(() => {
    if (isLoggedIn && currentUser && !isMobile && !sessionLoading) {
      const params = new URLSearchParams(search);
      const autoPlay = params.get('auto-play');

      if (autoPlay?.includes('video')) {
        showDialog(YoutubeDialog, {
          url: 'https://www.youtube.com/embed/jL658DXCT0Q?si=Amhw_cd63T1qs1py&autoplay=1&mute=1&enablejsapi=1',
          videoId: 'jL658DXCT0Q',
        });
      }

      if (autoPlay?.includes('game')) {
        handlePlayGameClick();
      }
    }
  }, [isLoggedIn, currentUser, isMobile, sessionLoading, search]);

  const iframeOrigin = useMemo(() => {
    const orig = IFRAME_PLAY_URL ?? '*';
    if (orig.endsWith('/')) return orig.slice(0, -1);
    else return orig;
  }, []);

  const onReceivedMessage = useCallback(
    (ev: MessageEvent) => {
      if (typeof window !== 'undefined') {
        // Note: events could come from multiple scripts,
        //   so we need to make sure the origin is the same as the iframe
        //
        if (ev.origin === iframeOrigin) {
          console.log(ev);
          if (ev.data) {
            let data: WebGLMessageData | string = ev.data;
            if (typeof data === 'string') {
              data = JSON.parse(data) as WebGLMessageData;
            }

            if (!data.type) {
              console.warn(
                "Received message from WebGL client without 'type':",
                data
              );
              return;
            }

            if (data.type && data.type === 'RELOAD_PORTAL') {
              window.location.reload();
            }
          }
        }
      }
    },
    [iframeOrigin]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', onReceivedMessage);

      return () => {
        window.removeEventListener('message', onReceivedMessage);
      };
    }
  }, [onReceivedMessage]);

  return (
    <>
      <div className="game-panel playgame-panel flex h-full items-center">
        {currentUser && startPlayGame ? (
          <iframe
            src={IFRAME_PLAY_URL}
            width="100%"
            height="100%"
            id="gameIframe"
            className="h-full w-full"
            style={{ border: 0 }}
            allow="clipboard-read; clipboard-write; local-network-access"
            title="Game Iframe"
            onLoad={(_ev) => {
              if (iframeRef.current?.contentWindow) {
                // For newer WebGL builds (> 1/2026)
                const sessionData = {
                  type: 'init',
                  data: {
                    session: currentUser.sessionToken,
                    nonce: currentUser.nonce,
                    deviceID: currentUser.deviceToken,
                    deviceToken: getTrackerInstance().tracker.getDeviceToken(),
                    facebookID: currentUser.fbId,
                    playerID: currentUser.playerId,
                    alias: currentUser.playerId,
                  },
                };
                iframeRef.current.contentWindow.postMessage(
                  JSON.stringify(sessionData),
                  iframeOrigin
                );
              }
            }}
            ref={iframeRef}
          />
        ) : (
          <div className="w-full px-10 text-center">
            <Image
              src="/assets/images/wwe-champions-logo-main.webp"
              className="mb-[30px] inline-block"
              alt="logo"
              width={170}
              height={105}
            />
            {shouldPlayOnMobileApp ? (
              <GameSectionIntroductionForMobileApp />
            ) : (
              <GameSectionIntroductionForWeb
                onPlayClick={handlePlayGameClick}
                isLoggedIn={isLoggedIn}
                sessionLoading={sessionLoading}
              />
            )}
          </div>
        )}
      </div>
      <LeavingPageDialog
        isPending={startPlayGame}
        onAccept={sendGameExitEvent}
      />
    </>
  );
};
